import { Properties, ReportConfig, TableHeader } from 'redux/reducers/reports'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { IDataFilterValue, IRetailConfig, ITag, ReportRangeType } from 'constants/interfaces'
import strings from 'constants/strings'
import { convertDateReportStringToDate, formatTimestamp } from 'utils'
import { transformDataForTable } from './components/DeepDive/Table/utils'
import { httpAuth } from 'config/apiClient'
import keys from 'constants/keys'
import endpoints from 'constants/endpoints'
import dayjs from 'dayjs'
import _ from 'lodash'

const GRAFANA_API =
  'https://grafana.apps-checkout.com:8899/d/fehl5ebd4311ce/prod?orgId=1&from=now-6h&to=now&timezone=browser'

export const ALLOWED_RETAILS_FOR_DYNAMIC_PARAMS = ['sportline']
interface CellData {
  value: string
  isTag: boolean
  groupId: string
  subRetailId: null
}

type ExtractValueResult = string | number | null | string[] | number[] | boolean

export const extractValueBySearchType = (data: any, rawReport: any) => {
  if (data === null || data === undefined) return null
  if (typeof data === 'string') return data
  if (typeof data === 'number') return data
  if (typeof data === 'boolean') return data

  try {
    switch (data.type) {
      case 'lookup':
        return rawReport[data.queryName] ? rawReport[data.queryName][data.index[0]][data.index[1]] : null
      case 'byRow':
        return rawReport[data.queryName]
          ? rawReport[data.queryName].slice(data.rowStartingIndex).map((row: any) => row[data.attributeColumnIndex])
          : null
      default:
        return null
    }
  } catch (error) {
    console.log({ extractValueBySearchType: error, data })
    return null
  }
}
export const extractValueByIndexType = <T extends ExtractValueResult>(data: any, rawReport: any): T => {
  if (data === undefined) {
    return null as T
  }
  if (data === null) return null as T
  if (typeof data === 'string') return data as T
  if (typeof data === 'number') return data as T

  try {
    switch (data.type) {
      case 'lookup':
        return rawReport[data.index[0]][data.index[1]] ?? (null as T)
      case 'byRow':
        return rawReport[data.attributeColumnIndex] ?? (null as T)
      default:
        return null as T
    }
  } catch (error) {
    console.log({ extractValueBySearchType: error, data })
    return null as T
  }
}

export const isStringOrNull = (value: unknown, variableName: string): value is string | null => {
  const isValid = typeof value === 'string' || value === null
  if (!isValid) {
    console.warn(`${variableName} string or null was expected but received :`, value)
  }
  return isValid
}

export const isNumberOrNull = (value: unknown, variableName: string): value is number | null => {
  const isValid = typeof value === 'number' || value === null
  if (!isValid) {
    console.log(`${variableName} --- number or null was expected but received :`, value)
  }
  return isValid
}

export const isNumber = (value: unknown, variableName: string): value is number => {
  const isValid = typeof value === 'number'
  if (!isValid) {
    console.log(`${variableName} --- number was expected but received :`, value)
  }
  return isValid
}

export const isString = (value: unknown, variableName: string): value is string => {
  const isValid = typeof value === 'string'
  if (!isValid) {
    console.log(`${variableName} --- string was expected but received :`, value)
  }
  return isValid
}

export const getReportDataLength = (isRepeatable?: boolean, dataSource?: string | null, reportData?: any) => {
  return isRepeatable && dataSource && reportData[dataSource] ? reportData[dataSource].length : 1
}

export const getTranslateX = (value: number, isRtl: boolean) => {
  if (value > 5) {
    return isRtl ? 'translateX(100%)' : 'translateX(-100%)'
  } else if (value < 5) {
    return isRtl ? 'translateX(20%)' : 'translateX(-20%)'
  }
}

export const countDigits = (number: number): number => {
  const numberStr = number.toString().replace('.', '').replace('-', '')
  return numberStr.length
}

export const getLineBarWidth = (value: number) => `${Math.min(value, 100)}%`

export const getReportValues = ({
  properties,
  reportData,
  index,
  isRepeatable,
  reportIndex,
}: {
  properties: Properties
  reportData: any
  index: number
  isRepeatable: boolean
  reportIndex: number
}): Partial<Record<keyof Properties, any>> => {
  const currentIndex = isRepeatable ? reportIndex : index
  const extractedValues = Object.entries(properties).reduce((acc, [key, propertyValue]) => {
    if (propertyValue !== undefined) {
      const value = extractValueBySearchType(propertyValue, reportData)

      if (Array.isArray(value)) {
        acc[key] = value[currentIndex] ?? null
      } else {
        acc[key] = value
      }
    }

    return acc
  }, {} as Partial<Record<keyof Properties, any>>)

  return extractedValues
}

export const normalizeNewlines = (input: string): string => {
  const regex = /,\n+/g
  return input.replace(regex, ',\n\n')
}

export const exportToExcel = async (
  data: CellData[][],
  headers: { text_val: string | null }[],
  locale: string,
  translations: object,
  fileName: string | null
) => {
  const filteredHeaders = headers.filter((header) => header.text_val !== null)

  const validHeaderIndices = filteredHeaders.map((header) => headers.indexOf(header))

  const filteredData = data.map((row) => row.filter((_, index) => validHeaderIndices.includes(index)))

  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('My Worksheet')

  const headerRow = worksheet.addRow(
    filteredHeaders.map((header) => translations[locale][header.text_val] || header.text_val)
  )
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, size: 14 }
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '90949C' },
    }
    cell.alignment = { horizontal: 'center' }
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    }
  })

  filteredData.forEach((row, rowIndex) => {
    const newRow = worksheet.addRow(row.map((cell) => cell.value))

    const firstCell = newRow.getCell(1)
    firstCell.font = { bold: true }
    firstCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '90949C' },
    }
    firstCell.alignment = { horizontal: 'left' }
    firstCell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    }
  })

  worksheet.columns.forEach((column) => {
    let maxLength = 0
    column.eachCell?.({ includeEmpty: true }, (cell) => {
      let textLength = cell.value?.toString().length || 0
      textLength += 2
      maxLength = Math.max(maxLength, textLength)
    })
    column.width = maxLength
  })

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  saveAs(blob, `${fileName ?? 'export'}.xlsx`)
}

export const exportToExcelWithTabs = async (
  finalResults: any[],
  headers: { text_val: string | null }[][],
  locale: string,
  translations: object,
  tabsNames: string[],
  fileName: string
) => {
  const workbook = new ExcelJS.Workbook()

  finalResults.forEach((result, index) => {
    const tabName = tabsNames[index]
    const worksheet = workbook.addWorksheet(`${tabName}`)
    const sheetHeaders = headers[index] || []

    const filteredHeaders = sheetHeaders.filter((header) => header.text_val !== null)

    const validHeaderIndices = filteredHeaders.map((header) => sheetHeaders.indexOf(header))

    const filteredData = result.map((row: any) => row.filter((_: any, idx: number) => validHeaderIndices.includes(idx)))

    const headerRow = worksheet.addRow(
      filteredHeaders.map((header) => translations[locale][header.text_val] || header.text_val)
    )

    headerRow.eachCell((cell) => {
      cell.font = { bold: true, size: 14 }
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '90949C' },
      }
      cell.alignment = { horizontal: 'center' }
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      }
    })

    filteredData.forEach((row: any) => {
      const newRow = worksheet.addRow(row.map((cell: any) => cell.value))

      const firstCell = newRow.getCell(1)
      firstCell.font = { bold: true }
      firstCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '90949C' },
      }
      firstCell.alignment = { horizontal: 'left' }
      firstCell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      }

      row.forEach((cell: any, cellIndex: number) => {
        if (cell && cell.value && typeof cell.value === 'string' && cell.value.includes('\n')) {
          const excelCell = newRow.getCell(cellIndex + 1)
          excelCell.alignment = {
            horizontal: 'right',
            vertical: 'top',
            wrapText: true,
          }
        }
      })
    })

    worksheet.columns.forEach((column) => {
      let maxLength = 0
      column.eachCell?.({ includeEmpty: true }, (cell) => {
        let textLength = cell.value?.toString().length || 0
        textLength += 2
        maxLength = Math.max(maxLength, textLength)
      })
      column.width = maxLength
    })
  })

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  saveAs(blob, `${fileName}.xlsx`)
}
interface IReportKeyParams {
  targetGroupId?: string
  groupId?: string | null
  subRetailId?: string | null
  rangeType?: string | number
  componentId?: string
  subComponentId?: string
  componentFilter?: string
  tag?: ITag | null
  selectedUsersIds?: string[]
  deepDiveSelectedUsersIds?: string[]
  deepDiveSelectedTag?: ITag | null
  targetDataFilter?: IDataFilterValue | null
  selectedDeepDiveDataFilter?: IDataFilterValue | null
}

export const generateReportKey = ({
  targetGroupId,
  groupId,
  subRetailId,
  rangeType,
  componentId,
  subComponentId,
  componentFilter,
  tag,
  selectedUsersIds,
  deepDiveSelectedUsersIds,
  deepDiveSelectedTag,
  targetDataFilter,
  selectedDeepDiveDataFilter,
}: IReportKeyParams) => {
  const usersIds =
    selectedUsersIds && selectedUsersIds.length
      ? selectedUsersIds.join(',')
      : deepDiveSelectedUsersIds && deepDiveSelectedUsersIds.length
      ? deepDiveSelectedUsersIds.join(',')
      : ''

  const tagSk = tag?.sk || deepDiveSelectedTag?.sk || ''

  const dataFilter = targetDataFilter?.value || selectedDeepDiveDataFilter?.value || ''

  return `${targetGroupId || groupId || ''}${subRetailId || ''}${rangeType || ''}${componentId || ''}${
    subComponentId || ''
  }${componentFilter || ''}${usersIds}${tagSk}${dataFilter}`
}

interface IGetGrafanaReportArgs {
  authorization: string
  groupId: string
  reportRangeType: number
  fromTs?: string
  toTs?: string
}
export const getGrafanaReport = ({ authorization, groupId, reportRangeType, fromTs, toTs }: IGetGrafanaReportArgs) => {
  const params: Record<string, string> = {
    'var-Authorization': authorization,
    'var-group': groupId,
    'var-report_range_type': reportRangeType.toString(),
    'var-from_ts': fromTs?.trim() ?? '',
    'var-to_ts': toTs?.trim() ?? '',
    theme: 'light',
    kiosk: '1',
    'var-is_deep_dive': 'true',
    'var-component_id': '15',
  }

  const query = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&')

  const finalUrl = `${GRAFANA_API}&${query}`
  console.log('grafana link: ' + finalUrl)
  return finalUrl
}

export const formatDateRange = (
  dateRange: string | undefined,
  is_support_us_date_format: boolean,
  is_calculate_utc_offset: boolean = false
) => {
  if (!dateRange || !dateRange.includes(' - ')) {
    return dateRange
  }

  const outputFormat = is_support_us_date_format
    ? strings.DATE_FORMATS.outputs.taskPeriodUS
    : strings.DATE_FORMATS.outputs.taskPeriod

  const [startDate, endDate] = dateRange.split(' - ')

  const formattedStartDate = formatTimestamp(startDate, outputFormat, 'DD/MM/YYYY', is_calculate_utc_offset)
  const formattedEndDate = formatTimestamp(endDate, outputFormat, 'DD/MM/YYYY', is_calculate_utc_offset)

  return `${formattedStartDate} - ${formattedEndDate}`
}

interface HandleGetDeepDiveFromExportedReportsProps {
  exportableReports: Properties[]
  subRetailId: string | null
  retailConfig: IRetailConfig | null
  selectedReportRangeType: ReportRangeType | null
  groupId: string
  tag?: ITag | null
  targetDataFilter?: IDataFilterValue | null
  selectedUsersIds: string[] | null
  locale: string
  translations: Record<string, any>
}

export const handleGetDeepDiveFromExportedReports = async ({
  exportableReports,
  subRetailId,
  retailConfig,
  selectedReportRangeType,
  groupId,
  tag,
  targetDataFilter,
  selectedUsersIds,
  locale,
  translations,
}: HandleGetDeepDiveFromExportedReportsProps) => {
  const url = endpoints.getReport
  const properties: Properties[] = exportableReports
  const sub_retail_id = subRetailId !== 'null' && !!subRetailId ? subRetailId : retailConfig?.sub_retail_id
  const isReportByDate = selectedReportRangeType?.type === keys.REPORT_DATE_NAMES.CUSTOM_DATE

  const startDate = isReportByDate
    ? convertDateReportStringToDate(selectedReportRangeType.title.split('-')[0])
    : undefined
  const endDate = isReportByDate
    ? convertDateReportStringToDate(selectedReportRangeType.title.split('-')[1])
    : undefined

  const formattedStartDate = startDate ? dayjs(startDate).format('YYYYMMDD') : ''
  const formattedEndDate = endDate ? dayjs(endDate).format('YYYYMMDD') : ''

  const requests = properties.map(async (property, index) => {
    const params = {
      group: groupId,
      report_range_type: property?.report_range_type || selectedReportRangeType?.type,
      is_deep_dive: true,
      ...(tag ? { tag: tag } : {}),
      ...(targetDataFilter ? { data_filter: targetDataFilter.value } : {}),
      ...(property.sub_component_id ? { sub_component_id: property.sub_component_id } : {}),
      ...(property.component_filter ? { component_filter: property.component_filter } : {}),
      ...(_.isNumber(property.component_id) ? { component_id: property.component_id } : {}),
      ...(sub_retail_id ? { sub_retail_id: sub_retail_id } : {}),
      ...(formattedStartDate ? { from_ts: formattedStartDate } : {}),
      ...(formattedEndDate ? { to_ts: formattedEndDate } : {}),
      ...(selectedUsersIds?.length ? { users: selectedUsersIds.join(',') } : {}),
    }
    try {
      const { data } = await httpAuth.get(url, { params })
      return { data, error: null, index }
    } catch (error) {
      console.error(`Error fetching data for property ${property.data_source}:`, error)
      return { data: null, error, index }
    }
  })
  const results = await Promise.allSettled(requests)
  const successfulResults = results
    .filter(
      (result): result is PromiseFulfilledResult<{ data: any; error: null; index: number }> =>
        result.status === 'fulfilled' && result.value.data
    )
    .map((result) => ({ data: result.value.data.reports, index: result.value.index }))

  const finalResults = properties.map((property, index) => {
    const result = successfulResults.find((res) => res.index === index)
    //@ts-ignore
    const title = translations[locale][property.deep_dive_component?.properties.title?.properties.text_val]
    const reportRangeTypeTitle =
      _.isNumber(property.report_range_type) && property.report_range_type !== 4
        ? //@ts-ignore
          translations[locale][keys.REPORT_DATE_OPTIONS_TRANSLATIONS[property.report_range_type]]
        : _.isNumber(selectedReportRangeType?.type) && selectedReportRangeType?.type !== 4
        ? //@ts-ignore
          translations[locale][keys.REPORT_DATE_OPTIONS_TRANSLATIONS[selectedReportRangeType?.type]]
        : ''

    const finalTitle = `${title}${reportRangeTypeTitle ? ` - ${reportRangeTypeTitle}` : ''}`

    return {
      rows: property.deep_dive_component?.properties.table_row as ReportConfig[],
      headers: property.deep_dive_component?.properties.table_header as TableHeader[],
      reportData: result ? result.data : null,
      dataSource: property.deep_dive_component?.properties.data_source,
      title: finalTitle,
      groupId: property.deep_dive_component?.properties.group_id,
      subRetailId: property.deep_dive_component?.properties.sub_retail_id,
    }
  })

  const headersForExcell = finalResults.map((res, index) =>
    (res.headers || []).map((header) => {
      return {
        text_val: extractValueByIndexType(header.text_val, successfulResults[index]?.data?.[0]?.[res.dataSource!]) as
          | string
          | null,
      }
    })
  )

  const rows = finalResults.map((res) => res.rows || [])

  let textTitleArr: string[] = []

  const preparedData = finalResults.map((res, index) => {
    const dataSource = res.dataSource
    if (res.reportData && res.reportData[0] && dataSource && res.reportData[0][dataSource]) {
      textTitleArr.push(extractValueByIndexType(res.title, res.reportData[0][dataSource]))

      return transformDataForTable(
        res.reportData[0][dataSource],
        rows[index],
        locale,
        translations,
        res.groupId,
        res.subRetailId
      )
    } else {
      textTitleArr.push(res.title || 'Unknown Report')
      return []
    }
  })
  return {
    preparedData,
    headersForExcell,
    textTitleArr,
  }
}
